import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  Badge,
  Button,
  confirm,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  FormInput,
  FormNumber,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import {
  CheckIcon,
  PackageIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import type {
  ProductionCostLineDTO,
  ProductionCostLineType,
} from "../../domain/contracts";
import {
  useCancelProductionCostLine,
  useCreateProductionCostLine,
  useDeleteProductionCostLine,
  useEventProductionCostsQuery,
  useMarkProductionCostLinePaid,
  useUpdateProductionCostLine,
} from "../api/queries";
import { EventProductionPayoutSection } from "./EventProductionPayoutSection";

const LINE_TYPE_OPTIONS: { value: ProductionCostLineType; label: string }[] = [
  { value: "prop", label: "Prop" },
  { value: "costume", label: "ຊຸດ" },
  { value: "material", label: "ວັດສະດຸ" },
  { value: "service", label: "ບໍລິການ" },
  { value: "other", label: "ອື່ນໆ" },
];

const LINE_TYPE_LABELS = Object.fromEntries(
  LINE_TYPE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<ProductionCostLineType, string>;

const ProductionCostFormSchema = z.object({
  lineType: z.enum(["prop", "costume", "material", "service", "other"]),
  description: z.string().trim().min(1, "ຕ້ອງປ້ອນລາຍລະອຽດ"),
  quantity: z.number().int().positive("ຈຳນວນຕ້ອງຫຼາຍກວ່າ 0"),
  unit: z.string().optional(),
  unitPrice: z.number().int().min(0, "ລາຄາຕ້ອງ ≥ 0"),
  vendorName: z.string().optional(),
  notes: z.string().optional(),
  markPaid: z.boolean(),
});

type ProductionCostFormValues = z.infer<typeof ProductionCostFormSchema>;

const STATUS_LABELS = {
  planned: "ວາງແຜນ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS = {
  planned: "secondary",
  paid: "default",
  cancelled: "destructive",
} as const;

function formatMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

type ProductionCostsSectionProps = {
  eventId: string;
};

type ProductionCostDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  editing: ProductionCostLineDTO | null;
};

function ProductionCostDialog({
  open,
  onOpenChange,
  eventId,
  editing,
}: ProductionCostDialogProps) {
  const createLine = useCreateProductionCostLine(eventId);
  const updateLine = useUpdateProductionCostLine(eventId);
  const isEdit = Boolean(editing);

  const methods = RHF.useForm<ProductionCostFormValues>({
    resolver: zodResolver(ProductionCostFormSchema),
    defaultValues: {
      lineType: "prop",
      description: "",
      quantity: 1,
      unit: "",
      unitPrice: 0,
      vendorName: "",
      notes: "",
      markPaid: false,
    },
  });

  useEffect(() => {
    if (!open) return;
    methods.reset({
      lineType: editing?.lineType ?? "prop",
      description: editing?.description ?? "",
      quantity: editing?.quantity ?? 1,
      unit: editing?.unit ?? "",
      unitPrice: editing?.unitPrice ?? 0,
      vendorName: editing?.vendorName ?? "",
      notes: editing?.notes ?? "",
      markPaid: false,
    });
  }, [open, editing, methods]);

  const quantity = methods.watch("quantity");
  const unitPrice = methods.watch("unitPrice");
  const previewSubtotal = useMemo(
    () => Math.max(0, Math.round((quantity || 0) * (unitPrice || 0))),
    [quantity, unitPrice],
  );

  const pending = createLine.isPending || updateLine.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "ແກ້ໄຂຕົ້ນທຸນ" : "ເພີ່ມຕົ້ນທຸນ"}
          </DialogTitle>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={(values) => {
            if (isEdit && editing) {
              updateLine.mutate(
                {
                  lineId: editing.id,
                  input: {
                    lineType: values.lineType,
                    description: values.description,
                    quantity: values.quantity,
                    unit: values.unit?.trim() || null,
                    unitPrice: values.unitPrice,
                    vendorName: values.vendorName?.trim() || null,
                    notes: values.notes?.trim() || null,
                  },
                },
                { onSuccess: () => onOpenChange(false) },
              );
              return;
            }
            createLine.mutate(
              {
                lineType: values.lineType,
                description: values.description,
                quantity: values.quantity,
                unit: values.unit?.trim() || null,
                unitPrice: values.unitPrice,
                vendorName: values.vendorName?.trim() || null,
                notes: values.notes?.trim() || null,
                markPaid: values.markPaid,
              },
              { onSuccess: () => onOpenChange(false) },
            );
          }}
          className="space-y-4"
        >
          <FormSelect
            name="lineType"
            label="ປະເພດ"
            options={LINE_TYPE_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
          />
          <FormInput name="description" label="ລາຍລະອຽດ" />
          <div className="grid grid-cols-2 gap-3">
            <FormNumber name="quantity" label="ຈຳນວນ" min={1} />
            <FormInput name="unit" label="ຫົວໜ່ວຍ (ຖ້າມີ)" />
          </div>
          <FormPrice
            name="unitPrice"
            currencySymbol="₭"
            label="ລາຄາຕໍ່ຫົວ"
            min={0}
          />
          <p className="-mt-2 text-muted-foreground text-xs">
            ລວມ: {formatMoney(previewSubtotal)}
          </p>
          <FormInput name="vendorName" label="ຜູ້ຂາຍ / ຮ້ານ (ຖ້າມີ)" />
          <FormTextarea name="notes" label="ໝາຍເຫດ" rows={2} />
          {!isEdit ? (
            <FormCheckboxCard
              name="markPaid"
              label="ບັນທຶກວ່າຈ່າຍແລ້ວທັນທີ"
              description="ເປີດໄດ້ຖ້າຈ່າຍແລ້ວຕອນເພີ່ມລາຍການ"
            />
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}

export function ProductionCostsSection({ eventId }: ProductionCostsSectionProps) {
  const canUpdate = useActionPermission(["performance-events:update"]);
  const query = useEventProductionCostsQuery(eventId);
  const markPaid = useMarkProductionCostLinePaid(eventId);
  const cancelLine = useCancelProductionCostLine(eventId);
  const deleteLine = useDeleteProductionCostLine(eventId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProductionCostLineDTO | null>(null);

  const items = query.data?.items ?? [];
  const summary = query.data?.summary;

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <PackageIcon className="size-4 text-primary" />
            <h2 className="font-semibold text-sm uppercase tracking-widest">
              Prop / ວັດສະດຸ
            </h2>
          </div>
          <p className="mt-1 text-muted-foreground text-xs">
            ບັນທຶກຕົ້ນທຸນ prop / ຊຸດ / ວັດສະດຸ · qty × ລາຄາ
          </p>
        </div>
        {canUpdate ? (
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <PlusIcon className="size-4" />
            ເພີ່ມລາຍການ
          </Button>
        ) : null}
      </div>

      <div className="flex-1 space-y-3 p-5">
        {summary && summary.lineCount > 0 ? (
          <div className="flex flex-wrap gap-3 text-sm">
            <div className="rounded-md border px-3 py-2">
              <p className="text-muted-foreground text-xs">ວາງແຜນ</p>
              <p className="font-semibold tabular-nums">
                {formatMoney(summary.plannedTotal)}
              </p>
            </div>
            <div className="rounded-md border px-3 py-2">
              <p className="text-muted-foreground text-xs">ຈ່າຍແລ້ວ</p>
              <p className="font-semibold tabular-nums">
                {formatMoney(summary.paidTotal)}
              </p>
            </div>
          </div>
        ) : null}

        {query.isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : items.length === 0 ? (
          <Empty className="border border-dashed py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <PackageIcon />
              </EmptyMedia>
              <EmptyTitle>ຍັງບໍ່ມີຕົ້ນທຸນ</EmptyTitle>
              <EmptyDescription>
                ເພີ່ມລາຍການ prop / ຊຸດ / ວັດສະດຸສຳລັບງານນີ້
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ul className="space-y-2">
            {items.map((row) => {
              const canEditRow = canUpdate && row.status === "planned";
              const canPayRow =
                canUpdate && row.status === "planned" && !row.linkedPayoutId;
              const canCancelRow = canUpdate && row.status === "planned";
              const canDeleteRow =
                canUpdate &&
                (row.status === "planned" || row.status === "cancelled");

              return (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{LINE_TYPE_LABELS[row.lineType]}</Badge>
                      <Badge variant={STATUS_VARIANTS[row.status]}>
                        {STATUS_LABELS[row.status]}
                      </Badge>
                    </div>
                    <p className="mt-1 font-semibold text-sm">{row.description}</p>
                    <p className="mt-0.5 font-semibold text-sm tabular-nums">
                      {formatMoney(row.subtotal)}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {row.quantity}
                      {row.unit ? ` ${row.unit}` : ""} × {formatMoney(row.unitPrice)}
                    </p>
                    {row.vendorName ? (
                      <p className="text-muted-foreground text-xs">
                        {row.vendorName}
                      </p>
                    ) : null}
                    {row.notes ? (
                      <p className="mt-1 text-muted-foreground text-xs">
                        {row.notes}
                      </p>
                    ) : null}
                    {row.linkedPayoutId ? (
                      <p className="mt-1 text-muted-foreground text-xs">
                        ຢູ່ໃນໃບເບີກຈ່າຍແລ້ວ
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {canEditRow ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditing(row);
                          setDialogOpen(true);
                        }}
                      >
                        <PencilIcon className="size-4" />
                        ແກ້ໄຂ
                      </Button>
                    ) : null}
                    {canPayRow ? (
                      <Button
                        type="button"
                        size="sm"
                        disabled={markPaid.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ບັນທຶກຈ່າຍຕົ້ນທຸນ",
                            description: `ຢືນຢັນວ່າຈ່າຍ ${formatMoney(row.subtotal)} ແລ້ວ?`,
                            actionText: "ຈ່າຍແລ້ວ",
                          });
                          if (ok) markPaid.mutate(row.id);
                        }}
                      >
                        <CheckIcon className="size-4" />
                        ຈ່າຍແລ້ວ
                      </Button>
                    ) : null}
                    {canCancelRow ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={cancelLine.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ຍົກເລີກລາຍການ",
                            description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກລາຍການນີ້?",
                            actionText: "ຍົກເລີກ",
                            ActionProps: { variant: "destructive" },
                          });
                          if (ok) cancelLine.mutate(row.id);
                        }}
                      >
                        <XIcon className="size-4" />
                        ຍົກເລີກ
                      </Button>
                    ) : null}
                    {canDeleteRow ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={deleteLine.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ລຶບລາຍການ",
                            description: "ລຶບລາຍການນີ້ອອກຖາວອນ?",
                            actionText: "ລຶບ",
                            ActionProps: { variant: "destructive" },
                          });
                          if (ok) deleteLine.mutate(row.id);
                        }}
                      >
                        <Trash2Icon className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <EventProductionPayoutSection eventId={eventId} costLines={items} />
      </div>

      <ProductionCostDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        eventId={eventId}
        editing={editing}
      />
    </div>
  );
}
